import { appConfig } from "../../../app.config";
import "./print-waiver.scss";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import {
  getWaiverCheckDefaults,
  renderWaiverCheckboxes,
} from "@shared/utility/waiver-checkbox";
import { MouseEvent } from "react";

/* eslint-disable-next-line */
export interface PrintWaiverProps {
  title: string;
  subTitle: string;
  fontSize: number;
  patient: string;
  doctor: string;
  service: string;
  waiverText: string;
  signature: string | null;
  session: number;
  // State of the "[ ]" checkboxes in waiverText; defaults to the author's
  // defaults when omitted.
  waiverChecks?: boolean[];
  // When provided the checkboxes are clickable; otherwise they are read-only.
  onToggleCheck?: (index: number) => void;
}

export function PrintWaiver({
  title,
  subTitle,
  fontSize,
  patient,
  doctor,
  service,
  waiverText,
  signature,
  session,
  waiverChecks,
  onToggleCheck,
}: PrintWaiverProps) {
  const { getAuthUser } = useUserIdentityContext();
  const slug = getAuthUser().slug;
  const logo = `${
    appConfig.bucketAssetPath
  }/${slug}/icon.png?${new Date().toISOString()}`;

  const formattedWaiver = waiverText
    .replace(/\[patient\]/g, patient)
    .replace(/\[doctor\]/g, doctor)
    .replace(/\[service\]/g, service)
    .replace(/\[session\]/g, session.toString());

  const defaultWaiverText = `MEDICAL SERVICE WAIVER\n\nI, <b>[patient]</b>, acknowledge that I have voluntarily elected to receive <b>[service]</b> from <b>[doctor]</b>. I understand that this service consists of <b>[session]</b> sessions and is being provided for my benefit.\n\nI understand and acknowledge the following:\n\nNature of Service: I have been informed about the purpose, benefits, and potential risks of <b>[service]</b>.\n\nVoluntary Participation: My decision to undergo this service is entirely voluntary, and I may discontinue treatment at any time.\n\nNo Guarantee of Results: I acknowledge that no guarantees have been made regarding the success or effectiveness of the treatment.\n\nAssumption of Risk: I accept all risks associated with this service and agree not to hold [doctor], their staff, or the medical facility liable for any adverse effects.\n\nInformed Consent: I confirm that I have had the opportunity to ask questions and receive answers regarding the service and that I understand all the information provided.\n\nBy signing below, I acknowledge that I have read, understood, and agree to this waiver.`;
  const displayWaiverText = renderWaiverCheckboxes(
    formattedWaiver ?? defaultWaiverText,
    waiverChecks ?? getWaiverCheckDefaults(waiverText),
    !!onToggleCheck
  );

  // Checkboxes are injected as HTML, so clicks are picked up by delegation.
  const handleWaiverClick = (event: MouseEvent<HTMLParagraphElement>) => {
    const target = event.target as HTMLElement;
    const index = target.getAttribute("data-waiver-check");

    if (onToggleCheck && index !== null) {
      onToggleCheck(Number(index));
    }
  };

  return (
    <div id="PrintWaiver" className="print-waiver">
      <style type="text/css" media="print">
        {
          "\
    @page { size: A4; }\
  "
        }
      </style>
      <div className="p-8 rounded-md page-break">
        <section className="flex justify-between">
          <div>
            <h2>{title}</h2>
            <h3>{subTitle}</h3>
          </div>
          <img src={logo} alt="Client Logo" />
        </section>
        <p
          style={{ fontSize: `${fontSize}px` }}
          onClick={handleWaiverClick}
          dangerouslySetInnerHTML={{
            __html: displayWaiverText.replace(/\n/g, "<br/>"),
          }}
        ></p>
        <section className="flex flex-col gap-6">
          <div className="w-min whitespace-nowrap">
            {signature ? (
              <div className="signature-preview">
                <img
                  src={signature}
                  alt={`signature`}
                  style={{
                    width: "150px",
                    height: "100px",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : null}
            <span className="border-b border-b-gray-400 font-bold">
              {patient}
            </span>
            <div>Patient Name</div>
          </div>

          <div className="w-min whitespace-nowrap">
            <span className="border-b border-b-gray-400 font-bold">
              {doctor}
            </span>
            <div>Doctor Name</div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PrintWaiver;
